/**
 * @group Network
 * @summary Base error messages for network errors
 * 
 * @remarks
 * This constant contains user-friendly error messages for various network errors,
 * providing consistent messaging across the application.
 * 
 * @example
 * ```typescript
 * import { NETWORK_ERROR_MESSAGES } from '@shared/constants';
 * 
 * function nextErrorResponse(error: Error): string {
 *   if (error.code === 'ECONNREFUSED') {
 *     return NETWORK_ERROR_MESSAGES.CONNECTION_REFUSED;
 *   }
 *   if (error.code === 'ETIMEDOUT') {
 *     return NETWORK_ERROR_MESSAGES.CONNECTION_TIMEOUT;
 *   }
 *   return NETWORK_ERROR_MESSAGES.UNKNOWN_ERROR;
 * }
 * ```
 */
export const NETWORK_ERROR_MESSAGES = {
  // Connection Errors
  CONNECTION_FAILED: 'Failed to establish connection to the server',
  CONNECTION_REFUSED: 'Connection was refused by the server',
  CONNECTION_RESET: 'Connection was reset by the server',
  CONNECTION_TIMEOUT: 'Connection timed out while trying to reach the server',
  
  // DNS Errors
  DNS_LOOKUP_FAILED: 'Failed to resolve server address',
  DNS_TIMEOUT: 'DNS lookup timed out',
  
  // SSL/TLS Errors
  SSL_HANDSHAKE_FAILED: 'SSL/TLS handshake failed',
  SSL_CERTIFICATE_ERROR: 'SSL certificate verification failed',
  
  // Request/Response Errors
  REQUEST_TIMEOUT: 'Request timed out',
  RESPONSE_TIMEOUT: 'Response timed out',
  INVALID_RESPONSE: 'Received invalid response from server',
  
  // Rate Limiting
  RATE_LIMITED: 'Too many requests. Please try again later',
  QUOTA_EXCEEDED: 'API quota exceeded',
  
  // Database Errors
  DATABASE_CONNECTION_FAILED: 'Failed to connect to database',
  DATABASE_TIMEOUT: 'Database operation timed out',
  DATABASE_UNAVAILABLE: 'Database is temporarily unavailable',
  
  // Authentication/Authorization
  INVALID_CREDENTIALS: 'Invalid credentials provided',
  TOKEN_EXPIRED: 'Authentication token has expired',
  INSUFFICIENT_PERMISSIONS: 'Insufficient permissions to access this resource',
  
  // Validation Errors
  INVALID_REQUEST_FORMAT: 'Invalid request format',
  MISSING_REQUIRED_FIELDS: 'Missing required fields in request',
  INVALID_FIELD_VALUE: 'One or more field values are invalid',
  
  // Generic Errors
  INTERNAL_ERROR: 'An internal server error occurred',
  SERVICE_UNAVAILABLE: 'Service is temporarily unavailable',
  MAINTENANCE_MODE: 'Service is under maintenance',
  UNKNOWN_ERROR: 'An unknown error occurred',
} as const; 